import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/lib/constants";
import { MessageCircle, X, Send, Bot, User, Loader2, Minimize2, UserCircle, Phone } from "lucide-react";

// Separate component file to avoid Babel issues
const ChatMessage = ({ message, isBot }) => {
  const containerClass = isBot ? '' : 'flex-row-reverse';
  const avatarClass = isBot ? 'bg-[#00FF85]/20' : 'bg-blue-500/20';
  const iconClass = isBot ? 'text-[#00FF85]' : 'text-blue-400';
  const bubbleClass = isBot 
    ? 'bg-white/5 text-gray-200 rounded-tl-sm' 
    : 'bg-[#00FF85] text-black rounded-tr-sm';

  return (
    <div className={`flex gap-3 ${containerClass}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${avatarClass}`}>
        {isBot ? <Bot className={`w-4 h-4 ${iconClass}`} /> : <User className={`w-4 h-4 ${iconClass}`} />}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${bubbleClass}`}>
        <p className="text-sm whitespace-pre-wrap">{message.text}</p>
      </div>
    </div>
  );
};

const LeadForm = ({ onSubmit, onSkip }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onSubmit({ name, email, phone });
    setSubmitting(false);
  };

  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10">
      <p className="text-sm text-gray-300 mb-3">
        Want to continue with our team? Leave your details:
      </p>
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input
          placeholder="Your Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white/5 border-white/10 text-white text-sm h-9"
          required
        />
        <Input
          type="email"
          placeholder="Email Address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-white/5 border-white/10 text-white text-sm h-9"
          required
        />
        <Input
          type="tel"
          placeholder="Phone (Optional)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="bg-white/5 border-white/10 text-white text-sm h-9"
        />
        <div className="flex gap-2">
          <Button 
            type="submit"
            className="flex-1 bg-[#00FF85] hover:bg-[#00FF85]/90 text-black text-sm h-9"
            disabled={submitting}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect with Team"}
          </Button>
          <Button 
            type="button"
            variant="ghost"
            className="text-gray-400 hover:text-white text-sm h-9"
            onClick={onSkip}
          >
            Later
          </Button>
        </div>
      </form>
    </div>
  );
};

const QuickActions = ({ onAction }) => (
  <div className="flex flex-wrap gap-2 mt-2">
    <button onClick={() => onAction("Tell me about your SEO services")} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300">
      SEO Services
    </button>
    <button onClick={() => onAction("What is AEO and GEO?")} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300">
      AEO & GEO
    </button>
    <button onClick={() => onAction("What are your pricing options?")} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300">
      Pricing
    </button>
    <button onClick={() => onAction("How can I get started?")} className="text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300">
      Get Started
    </button>
  </div>
);

const TypingIndicator = () => (
  <div className="flex gap-3">
    <div className="w-8 h-8 rounded-full bg-[#00FF85]/20 flex items-center justify-center">
      <Bot className="w-4 h-4 text-[#00FF85]" />
    </div>
    <div className="bg-white/5 rounded-2xl rounded-tl-sm px-4 py-3">
      <div className="flex gap-1">
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
      </div>
    </div>
  </div>
);

// Human Escalation Form Component
const HumanEscalationForm = ({ onSubmit, onCancel, chatHistory }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await onSubmit({ name, email, phone });
    setSubmitting(false);
  };

  return (
    <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-xl p-4 border border-blue-500/30">
      <div className="flex items-center gap-2 mb-3">
        <UserCircle className="w-5 h-5 text-blue-400" />
        <p className="text-sm font-medium text-white">Connect with Our Team</p>
      </div>
      <p className="text-xs text-gray-400 mb-3">
        Share your details and we'll have a team member reach out within minutes during business hours.
      </p>
      <form onSubmit={handleSubmit} className="space-y-2">
        <Input
          placeholder="Your Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="bg-white/5 border-white/10 text-white text-sm h-9"
          required
        />
        <Input
          type="email"
          placeholder="Email Address *"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="bg-white/5 border-white/10 text-white text-sm h-9"
          required
        />
        <Input
          type="tel"
          placeholder="Phone (for faster response)"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="bg-white/5 border-white/10 text-white text-sm h-9"
        />
        <div className="flex gap-2 pt-1">
          <Button 
            type="submit"
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm h-9"
            disabled={submitting}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request Human Chat"}
          </Button>
          <Button 
            type="button"
            variant="ghost"
            className="text-gray-400 hover:text-white text-sm h-9 px-3"
            onClick={onCancel}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

const INITIAL_MESSAGE = {
  id: 1,
  text: "Hi! I'm the Digital Media Ninja AI assistant. I can help you learn about our SEO, AEO, and GEO services. What would you like to know?",
  isBot: true
};

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showHumanEscalation, setShowHumanEscalation] = useState(false);
  const [hasSubmittedLead, setHasSubmittedLead] = useState(false);
  const [hasRequestedHuman, setHasRequestedHuman] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const stored = localStorage.getItem('chatSessionId');
    if (stored) {
      setSessionId(stored);
    } else {
      const newId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('chatSessionId', newId);
      setSessionId(newId);
    }
  }, []);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([INITIAL_MESSAGE]);
    }
  }, [isOpen, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized]);

  const sendMessage = async (text) => {
    if (!text.trim() || isLoading) return;

    const userMsg = { id: Date.now(), text: text.trim(), isBot: false };
    setMessages(prev => [...prev, userMsg]);
    setInputValue("");
    setIsLoading(true);

    try {
      const res = await axios.post(`${API}/chatbot/message`, {
        message: text.trim(),
        sessionId: sessionId
      });

      const botMsg = { id: Date.now() + 1, text: res.data.response, isBot: true };
      setMessages(prev => [...prev, botMsg]);

      if (messages.length >= 4 && !hasSubmittedLead && !showLeadForm && res.data.suggestLeadCapture) {
        setTimeout(() => setShowLeadForm(true), 1000);
      }
    } catch (err) {
      const errorMsg = { id: Date.now() + 1, text: "Sorry, I'm having trouble. Please try again or email info@digitalmedianinja.com", isBot: true };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLeadSubmit = async (data) => {
    try {
      await axios.post(`${API}/chatbot/lead`, {
        ...data,
        sessionId: sessionId,
        chatHistory: messages.map(m => ({ text: m.text, isBot: m.isBot }))
      });
      setHasSubmittedLead(true);
      setShowLeadForm(false);
      const thankMsg = { id: Date.now(), text: `Thanks ${data.name.split(' ')[0]}! Our team will reach out soon. Keep asking questions!`, isBot: true };
      setMessages(prev => [...prev, thankMsg]);
    } catch (err) {
      console.error("Lead submit error:", err);
    }
  };

  // Handle human escalation request
  const handleHumanEscalation = async (data) => {
    try {
      // Submit escalation request to backend
      await axios.post(`${API}/chatbot/escalate`, {
        ...data,
        sessionId: sessionId,
        chatHistory: messages.map(m => ({ text: m.text, isBot: m.isBot })),
        escalationType: "human_chat_request"
      });
      
      setHasRequestedHuman(true);
      setShowHumanEscalation(false);
      
      // Add confirmation message
      const confirmMsg = { 
        id: Date.now(), 
        text: `Great news, ${data.name.split(' ')[0]}! 🎉\n\nA team member will reach out to you shortly. During business hours (Mon-Fri, 9am-5pm CT), expect a response within 15 minutes.\n\nIn the meantime, I'm still here to answer any questions!`,
        isBot: true 
      };
      setMessages(prev => [...prev, confirmMsg]);
      
      // Try to open HubSpot chat if available
      if (window.HubSpotConversations && window.HubSpotConversations.widget) {
        try {
          window.HubSpotConversations.widget.open();
        } catch (e) {
          console.log("HubSpot chat widget not available");
        }
      }
    } catch (err) {
      console.error("Escalation error:", err);
      const errorMsg = { 
        id: Date.now(), 
        text: "I've noted your request. Please email us at info@digitalmedianinja.com and we'll get back to you right away!", 
        isBot: true 
      };
      setMessages(prev => [...prev, errorMsg]);
      setShowHumanEscalation(false);
    }
  };

  // Trigger human escalation flow
  const initiateHumanChat = () => {
    setShowHumanEscalation(true);
    setShowLeadForm(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  // Floating button when closed
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-6 w-14 h-14 rounded-full bg-[#00FF85] hover:bg-[#00FF85]/90 text-black shadow-lg flex items-center justify-center transition-all hover:scale-110 z-[9999]"
        data-testid="chatbot-toggle"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  // Minimized bar
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-24 right-6 w-72 bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-xl z-[9999] cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#00FF85]/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-[#00FF85]" />
            </div>
            <div>
              <p className="text-white font-medium text-sm">DMN Assistant</p>
              <p className="text-gray-400 text-xs">Click to expand</p>
            </div>
          </div>
          <button onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  // Full chat window
  return (
    <div className="fixed bottom-24 right-6 w-96 h-[600px] max-h-[80vh] bg-[#0A0A0A] border border-white/10 rounded-2xl shadow-xl flex flex-col overflow-hidden z-[9999]">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-gradient-to-r from-[#00FF85]/10 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#00FF85]/20 flex items-center justify-center">
            <Bot className="w-5 h-5 text-[#00FF85]" />
          </div>
          <div>
            <p className="text-white font-medium">DMN AI Assistant</p>
            <p className="text-xs text-[#00FF85] flex items-center gap-1">
              <span className="w-2 h-2 bg-[#00FF85] rounded-full animate-pulse"></span>
              Online
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setIsMinimized(true)} className="text-gray-400 hover:text-white p-1">
            <Minimize2 className="w-5 h-5" />
          </button>
          <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => (
          <ChatMessage key={msg.id} message={msg} isBot={msg.isBot} />
        ))}
        {isLoading && <TypingIndicator />}
        {showLeadForm && !hasSubmittedLead && (
          <LeadForm onSubmit={handleLeadSubmit} onSkip={() => setShowLeadForm(false)} />
        )}
        {showHumanEscalation && !hasRequestedHuman && (
          <HumanEscalationForm 
            onSubmit={handleHumanEscalation} 
            onCancel={() => setShowHumanEscalation(false)}
            chatHistory={messages}
          />
        )}
        {messages.length === 1 && <QuickActions onAction={sendMessage} />}
        <div ref={messagesEndRef} />
      </div>

      {/* Chat to Human Button */}
      {!hasRequestedHuman && !showHumanEscalation && messages.length > 1 && (
        <div className="px-4 pb-2">
          <button
            onClick={initiateHumanChat}
            className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:text-blue-300 text-sm transition-all"
            data-testid="chat-to-human-btn"
          >
            <UserCircle className="w-4 h-4" />
            Chat with a Human
          </button>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            disabled={isLoading}
          />
          <Button
            onClick={() => sendMessage(inputValue)}
            disabled={!inputValue.trim() || isLoading}
            className="bg-[#00FF85] hover:bg-[#00FF85]/90 text-black"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-500 text-center mt-2">Powered by Claude AI</p>
      </div>
    </div>
  );
};

export default AIChatbot;
